// Pure formatters + design tokens used by every dashboard card.
// Lives alongside real.tsx so it's obvious which page consumes it.
import { ClipboardTextIcon, CoinsIcon, PackageIcon, ShoppingCartIcon } from "@phosphor-icons/react"
import { i18n } from "@dehub/theme"
import { toneOfColor, type StatusTone } from "../../components/ui/status-pill"

// Fallback only. /ord/dashboard/main already sends a colour per status row
// (ORDER_STATUS[...].color), so OrderStatusCard reads that first; this map is
// what keeps a row from going grey if an older backend omits it. Keys are the
// names the backend actually sends — sentName / receivedName, Title-case. The
// previous ALL-CAPS keys never matched, so every status rendered grey.
export const STATUS_COLOR: Record<string, string> = {
  "Түр төлөв"            : "var(--mantine-color-gray-4)",
  "Бүртгэсэн"            : "var(--mantine-color-blue-5)",
  "Хяналтаас татгалзсан" : "var(--mantine-color-gray-7)",
  "Хянаад илгээсэн"      : "var(--mantine-color-indigo-7)",
  "Ирсэн"                : "var(--mantine-color-indigo-7)",
  "Цуцалсан"             : "var(--mantine-color-pink-6)",
  "Баталсан"             : "var(--mantine-color-green-filled)",
  "Баталгаажсан"         : "var(--mantine-color-green-filled)",
  "Татгалзсан"           : "var(--mantine-color-red-7)",
  "Авто цуцлагдсан"      : "var(--mantine-color-violet-7)",
  "Бэлтгэж байна"        : "var(--mantine-color-yellow-6)",
  "Хүргэж байна"         : "var(--mantine-color-yellow-5)",
  "Хүргэсэн"             : "var(--mantine-color-green-filled)",
  "Дууссан"              : "var(--mantine-color-green-filled)",
}

export interface StatConfig {
  icon: typeof ShoppingCartIcon
  isCurrency: boolean
  labelKey: string
  /** Used instead of labelKey on the BUYER portal, where the same figure is
   *  the business's spend, not its sales. */
  buyerLabelKey?: string
}

// Small icon before each KPI label (dark on gray, see real.module.css .statIcon).
export const STAT_CONFIGS: StatConfig[] = [
  { icon: ShoppingCartIcon,  isCurrency: true,  labelKey: "stats.salesOrder", buyerLabelKey: "stats.purchaseOrder" },
  { icon: ClipboardTextIcon, isCurrency: false, labelKey: "stats.orderCount" },
  { icon: PackageIcon,       isCurrency: false, labelKey: "stats.orderItems" },
  { icon: CoinsIcon,         isCurrency: true,  labelKey: "stats.averageOrder" },
]

export function formatCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}${i18n.t("dashboard:numberFormat.million")}`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}${i18n.t("dashboard:numberFormat.thousand")}`
  return value.toLocaleString()
}

export function formatCurrencyShort(value: number): { num: string; unit: string } {
  if (value >= 1_000_000) return { num: (value / 1_000_000).toFixed(2), unit: `${i18n.t("dashboard:numberFormat.million")} ₮` }
  if (value >= 1_000) return { num: (value / 1_000).toFixed(0), unit: `${i18n.t("dashboard:numberFormat.thousand")} ₮` }
  return { num: value.toLocaleString(), unit: "₮" }
}

export interface FormattedChange {
  label: string
  isPositive: boolean
  isNew: boolean
  /** Rounds to 0.0% — shown as words, not as "↑ 0.0%". */
  isFlat: boolean
  /** The response carried no percentage at all — an older backend. Better an
   *  em dash than a number the card would be making up. */
  isUnknown: boolean
}

// Takes the month-over-month percentage, NOT the absolute delta. Reading the
// delta here is what printed a 4.5M₮ rise as "4500000.00%", and then — once a
// sanity cap was bolted on — as "Шинэ" for every KPI that moved at all.
export function formatChange(changePct: number | null | undefined): FormattedChange {
  if (changePct === undefined) {
    return { label: "-", isPositive: true, isNew: false, isFlat: false, isUnknown: true }
  }
  if (changePct === null || !isFinite(changePct)) {
    return { label: i18n.t("dashboard:numberFormat.new"), isPositive: true, isNew: true, isFlat: false, isUnknown: false }
  }
  return {
    label     : `${Math.abs(changePct).toFixed(1)}%`,
    isPositive: changePct >= 0,
    isNew     : false,
    isFlat    : Math.abs(changePct) < 0.05,
    isUnknown : false,
  }
}

export type RecentOrderStatusVariant = "confirmed" | "delivering" | "delivered" | "preparing"

// Matched against the names /ord/dashboard/main sends (ORDER_STATUS sentName /
// receivedName). These are Title-case; comparing them to ALL-CAPS literals, as
// this used to, sent every row down the "preparing" branch.
export function statusVariant(status: string | undefined | null): RecentOrderStatusVariant {
  if (status === "Баталгаажсан" || status === "Баталсан") return "confirmed"
  if (status === "Хүргэж байна") return "delivering"
  if (status === "Хүргэсэн" || status === "Дууссан") return "delivered"
  return "preparing"
}

/**
 * Status name → shared status pill tone (confirmed and delivered are both the one green).
 * The name wins over `color`: the backend's «Хүргэсэн» colour is a teal hex that
 * toneOfColor reads as blue, while the recent-orders pill on the same page is green.
 * Statuses the name map does not know fall back to the colour's tone, else gray.
 */
export function statusTone(status: string | undefined | null, color?: string | null): StatusTone {
  const v = statusVariant(status)
  if (v === "confirmed" || v === "delivered") return "green"
  if (v === "delivering") return "yellow"
  return color ? toneOfColor(color) : "gray"
}
